import { Component, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Validators } from '@angular/forms';
import { ListItem } from '@lmnop/web-core';
import { CoreService_TOKEN, CustomValidators, ExtendedFormControl, ExtendedFormGroup, ICoreService, MODULE_ENVIRONMENTS } from 'angular-core-src';
import { ControlBase } from '../../base/control-base/control-base.component';
import gsap from 'gsap';
import { NgxSpinnerService } from 'ngx-spinner';
import { firstValueFrom } from 'rxjs';
import { ContactUsMessageModel } from '../../../types/contact-us-message.model';

@Component({
    selector: 'contact-form-control',
    templateUrl: 'contact-form.control.html',
    styleUrl: './contact-form.control.scss',
    standalone: false,
})
export class ContactFormControl extends ControlBase {
    constructor(
        @Inject(CoreService_TOKEN)
        private coreService: ICoreService,
        @Inject(MODULE_ENVIRONMENTS)
        private environment: any,
        private httpClient: HttpClient,
        protected override spinnerService: NgxSpinnerService
    ) {
        super(spinnerService);
    }

    public contactForm: ExtendedFormGroup<ContactUsMessageModel> = this.getFormGroup();

    public typeList: ListItem[] = [
        {
            value: 'development',
            text: 'contact_form.development',
        },
        {
            value: 'extension',
            text: 'contact_form.extension',
        },
        {
            value: 'other',
            text: 'contact_form.other',
        },
    ];

    public ngAfterViewInit(): void {
        this.init();
    }

    private init() {
        this.contactForm = this.getFormGroup();
        $('#contact-form-tab').tabSlideOut({
            tabLocation: 'right',
            offset: '70px',
        });
    }

    public typeChange(event: any) {
        const target = event.target as HTMLSelectElement;
        const type = target.value;

        //this is not translated on purpose, we use english as communication language
        let subject: string;
        switch (type) {
            case '':
                subject = '';
                break;
            case 'extension':
                subject = 'Extension related inquiry';
                break;
            case 'development':
                subject = 'We need web development';
                break;
            case 'other':
                subject = '';
                break;
            default:
                throw new Error('Invalid contact type');
        }
        this.contactForm.setControlValue('subject', subject);
    }

    private getFormGroup() {
        let res = new ExtendedFormGroup('contact-form', ContactUsMessageModel, {
            type: new ExtendedFormControl('type', 'development', [Validators.required]),
            subject: new ExtendedFormControl('subject', 'We need web development', [Validators.required, Validators.minLength(5), Validators.maxLength(50)]),
            senderName: new ExtendedFormControl('senderName', '', [Validators.required, Validators.minLength(5), Validators.maxLength(50)]),
            senderEmail: new ExtendedFormControl('senderEmail', '', [Validators.required, Validators.maxLength(100), CustomValidators.email()]),
            body: new ExtendedFormControl('body', '', [Validators.required, Validators.minLength(10), Validators.maxLength(2000)]),
        });
        return res;
    }

    private errorHappenedDuringMailSending: boolean = false;
    public sendMessage = async (): Promise<void> => {
        if (this.contactForm.validateAllFormFields()) {
            this.startButtonAnimation();
            // let contactUsEmailModel: ContactUsMessageModel = new ContactUsMessageModel();

            let formValue: any = this.contactForm.getValue() as ContactUsMessageModel;

            // contactUsEmailModel.type = formValue['type'];
            // contactUsEmailModel.body = formValue['body'];
            // contactUsEmailModel.subject = formValue['subject'];
            // contactUsEmailModel.senderName = formValue['senderName'];
            // contactUsEmailModel.senderEmail = formValue['senderEmail'];

            //originally there were no error case in the animation, so we have to handle that case with jquery to show/hide the error/sending titles
            this.hideErrorButtonMessage();
            this.errorHappenedDuringMailSending = false;

            await this.handleSpinner(
                firstValueFrom(this.coreService.sendContactUsMessage(formValue))
                    .then((): void => {
                        this.ioc.core.notificationService.createTimedSuccessTranslateMessage('common.email_sent');
                        this.contactForm = this.getFormGroup();
                        this.resetButtonAnimation();
                    })
                    .catch((): void => {
                        this.errorHappenedDuringMailSending = true;
                        this.showErrorButtonMessage();
                        this.resetButtonAnimation();
                    })
            );
        }
    };

    private showErrorButtonMessage(): void {
        let $button = $('#send-button');
        let $successSpan = $button.find('.success');
        $successSpan.hide();
        let $errorSpan = $button.find('.error');
        $errorSpan.show();
    }

    private hideErrorButtonMessage(): void {
        let $button = $('#send-button');
        let $errorSpan = $button.find('.error');
        $errorSpan.hide();
    }

    private resetButtonAnimation(): void {
        let button = document.getElementById('send-button');
        setTimeout(() => {
            button.removeAttribute('style');
            gsap.fromTo(
                button,
                {
                    opacity: 0,
                    y: -8,
                },
                {
                    opacity: 1,
                    y: 0,
                    clearProps: true,
                    duration: 0.3,
                    onComplete() {
                        button.classList.remove('active');
                    },
                }
            );
        }, 3000);
    }

    private startButtonAnimation(): void {
        let button = document.getElementById('send-button');
        let getVar = variable => getComputedStyle(button).getPropertyValue(variable);
        let that = this;
        if (!button.classList.contains('active')) {
            button.classList.add('active');

            gsap.to(button, {
                keyframes: [
                    {
                        '--left-wing-first-x': 50,
                        '--left-wing-first-y': 100,
                        '--right-wing-second-x': 50,
                        '--right-wing-second-y': 100,
                        duration: 0.2,
                        onComplete() {
                            gsap.set(button, {
                                '--left-wing-first-y': 0,
                                '--left-wing-second-x': 40,
                                '--left-wing-second-y': 100,
                                '--left-wing-third-x': 0,
                                '--left-wing-third-y': 100,
                                '--left-body-third-x': 40,
                                '--right-wing-first-x': 50,
                                '--right-wing-first-y': 0,
                                '--right-wing-second-x': 60,
                                '--right-wing-second-y': 100,
                                '--right-wing-third-x': 100,
                                '--right-wing-third-y': 100,
                                '--right-body-third-x': 60,
                            });
                        },
                    },
                    {
                        '--left-wing-third-x': 20,
                        '--left-wing-third-y': 90,
                        '--left-wing-second-y': 90,
                        '--left-body-third-y': 90,
                        '--right-wing-third-x': 80,
                        '--right-wing-third-y': 90,
                        '--right-body-third-y': 90,
                        '--right-wing-second-y': 90,
                        duration: 0.2,
                    },
                    {
                        '--rotate': 50,
                        '--left-wing-third-y': 95,
                        '--left-wing-third-x': 27,
                        '--right-body-third-x': 45,
                        '--right-wing-second-x': 45,
                        '--right-wing-third-x': 60,
                        '--right-wing-third-y': 83,
                        duration: 0.25,
                    },
                    {
                        '--rotate': 55,
                        '--plane-x': -8,
                        '--plane-y': 24,
                        duration: 0.2,
                    },
                    {
                        '--rotate': 40,
                        '--plane-x': 45,
                        '--plane-y': -180,
                        '--plane-opacity': 0,
                        duration: 0.3,
                        onComplete() {
                            if (that.errorHappenedDuringMailSending) {
                                that.showErrorButtonMessage();
                            }
                        },
                    },
                ],
            });

            if (!this.errorHappenedDuringMailSending) {
                gsap.to(button, {
                    keyframes: [
                        {
                            '--text-opacity': 0,
                            '--border-radius': 0,
                            '--left-wing-background': getVar('--primary-darkest'),
                            '--right-wing-background': getVar('--primary-darkest'),
                            duration: 0.1,
                        },
                        {
                            '--left-wing-background': getVar('--primary'),
                            '--right-wing-background': getVar('--primary'),
                            duration: 0.1,
                        },
                        {
                            '--left-body-background': getVar('--primary-dark'),
                            '--right-body-background': getVar('--primary-darkest'),
                            duration: 0.4,
                        },
                        {
                            '--success-opacity': 1,
                            '--success-scale': 1,
                            duration: 0.25,
                            delay: 0.25,
                        },
                    ],
                });
            }
        }
    }
}
